'use client';

import type { IconButtonProps } from '@mui/material/IconButton';

import { useState, useCallback, useContext, useEffect, useMemo } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AccountButton } from './account-button';
import { Iconify } from '../../iconify';
import { SessionContext } from '@/app/components/Session';
import Link from 'next/link';
import { AuthContext } from '@/app/components/Authentication';
import { BETA_ACCESS, useBetaAccess } from '@/hooks/useBetaAccess';
import { getImpersonationSession } from '@/services/userLoginApi';
import { ImpersonationContext } from '@/app/components/ImpersonationContext';
import { useAdminCheck } from '@/hooks/useAdminCheck';
import { useRBAC } from '@/contexts/RBACContext';
import MailhouseAxios from '@/app/components/MailhouseAxios';
import { useMailhouseApi } from '@/hooks/useMailhouseApi';
import { openPlatformOnboarding, useCanSeePlatformOnboarding } from './platform-onboarding';

/**
 * Separate child component (not inlined into AccountDrawer's own body) so it
 * can sit INSIDE the <MailhouseAxios> provider AccountDrawer renders below —
 * a component can't consume a context it also provides in the same render.
 * Mints a fresh Partner Portal bearer token via POST /admin/staff-portal-access
 * and opens the returned URL directly (token embedded in the query string) —
 * the staff member never sees a login screen.
 */
function MailhouseAdminMenuItem({ onNavigate }: { onNavigate: () => void }) {
  const mailhouseApi = useMailhouseApi();
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = useCallback(async () => {
    setIsLoading(true);
    // Open the tab synchronously, still inside the click handler, so
    // browsers treat it as the trusted user gesture rather than a popup —
    // opening it AFTER the `await` below is what was getting blocked, since
    // by the time the token request resolves the browser no longer
    // considers the resulting window.open() part of the original click.
    // Dropping noopener/noreferrer here is required to keep a handle we can
    // navigate once the token request resolves; acceptable since this only
    // ever opens our own Mailhouse portal, never third-party content.
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.title = 'Opening Mailhouse Portal…';
    }
    try {
      const { portal_url } = await mailhouseApi.getStaffPortalAccess();
      if (newWindow) {
        newWindow.location.href = portal_url;
      } else {
        // eslint-disable-next-line no-console
        console.error('Mailhouse Portal tab was blocked by the browser.');
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to get Mailhouse Portal access', err);
      newWindow?.close();
    } finally {
      setIsLoading(false);
      onNavigate();
    }
  }, [mailhouseApi, onNavigate]);

  return (
    <MenuItem
      onClick={handleClick}
      disabled={isLoading}
      sx={{ borderRadius: 1, px: 1, py: 0.75 }}
    >
      <Iconify icon="solar:letter-bold" width={18} sx={{ mr: 1.5 }} />
      {isLoading ? 'Opening Mailhouse Portal…' : 'Mailhouse Admin'}
    </MenuItem>
  );
}

export type AccountDrawerProps = IconButtonProps & {
  data?: {
    label: string;
    href: string;
    icon?: React.ReactNode;
    info?: React.ReactNode;
  }[];
};

export function AccountDrawer({ data = [], sx, ...other }: AccountDrawerProps) {
  const theme = useTheme();

  const { logout, userId } = useContext(AuthContext);
  const { isImpersonating } = useContext(ImpersonationContext);
  const { isAdmin } = useAdminCheck();
  const { userContext, hasPermission } = useRBAC();
  const canSeePlatformTour = useCanSeePlatformOnboarding();
  const canAccessMailhouse = hasPermission('mailhouse.manage');
  // Sub-account members only (parent_account_id set). Root-account members share the org wallet.
  const isSubAccountMember = useMemo(() => {
    return (
      Boolean(userContext) &&
      !userContext?.is_org_owner &&
      userContext?.parent_account_id != null
    );
  }, [userContext]);

  const { user } = useContext(SessionContext);
  const [sessionCanImpersonate, setSessionCanImpersonate] = useState(false);
  const hasImpersonationPermission = useBetaAccess(BETA_ACCESS.impersonationUse);
  const canImpersonate =
    sessionCanImpersonate || hasImpersonationPermission;
  const name = useMemo(() => {
    if (user) {
      let data = user.firstname;
      if (user.lastname) {
        data += ' ' + user.lastname;
      }
      return data;
    } else {
      return '';
    }
  }, [user]);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleOpenPopover = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    },
    []
  );

  const handleClosePopover = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const open = Boolean(anchorEl);

  useEffect(() => {
    let mounted = true;

    getImpersonationSession()
      .then((session) => {
        if (mounted) setSessionCanImpersonate(Boolean(session.can_impersonate));
      })
      .catch(() => {
        if (mounted) setSessionCanImpersonate(false);
      });

    return () => {
      mounted = false;
    };
  }, [userId]);

  const onLogout = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      handleClosePopover();
      logout();
    },
    [logout, handleClosePopover]
  );

  return (
    <>
      <Tooltip title="Tenant">
        <AccountButton
          onClick={handleOpenPopover}
          photoURL={user?.photoURL}
          displayName={user?.displayName}
          sx={sx}
          {...other}
        />
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              width: 200,
              p: 0,
              mt: 1,
              ml: 0.5,
              borderRadius: 1.5,
              boxShadow: theme.shadows[12],
            },
          },
        }}
      >
        <Box sx={{ p: 1.5, pb: 1 }}>
          <Typography variant="subtitle2" noWrap fontWeight={600}>
            {name}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'text.primary', mt: 0.25 }}
            noWrap
          >
            {user?.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 0.5 }}>
          <MenuItem
            component={Link}
            href={`/account/personal-details`}
            onClick={handleClosePopover}
            sx={{ borderRadius: 1, px: 1, py: 0.75 }}
          >
            <Iconify icon="solar:user-bold" width={18} sx={{ mr: 1.5 }} />
            Personal Details
          </MenuItem>

          <MenuItem
            component={Link}
            href={'/account/change-password'}
            onClick={handleClosePopover}
            sx={{ borderRadius: 1, px: 1, py: 0.75 }}
          >
            <Iconify
              icon="solar:lock-password-bold"
              width={18}
              sx={{ mr: 1.5 }}
            />
            Change Password
          </MenuItem>

          <MenuItem
            component={Link}
            href={'/account/security'}
            onClick={handleClosePopover}
            sx={{ borderRadius: 1, px: 1, py: 0.75 }}
          >
            <Iconify icon="solar:shield-keyhole-bold" width={18} sx={{ mr: 1.5 }} />
            Two-Factor Authentication
          </MenuItem>

          <MenuItem
            component={Link}
            href={'/account/active-orders'}
            onClick={handleClosePopover}
            sx={{ borderRadius: 1, px: 1, py: 0.75 }}
          >
            <Iconify icon="solar:bag-bold" width={18} sx={{ mr: 1.5 }} />
            Orders
          </MenuItem>

          <MenuItem
            component={Link}
            href={
              isSubAccountMember
                ? '/account/wallet/sub-account'
                : '/account/wallet'
            }
            onClick={handleClosePopover}
            sx={{ borderRadius: 1, px: 1, py: 0.75 }}
          >
            <Iconify icon="solar:wallet-bold" width={18} sx={{ mr: 1.5 }} />
            {isSubAccountMember ? 'Account Wallet' : 'My Wallet'}
          </MenuItem>

          {canImpersonate && (
            <MenuItem
              component={Link}
              href={'/account/switch-user'}
              onClick={handleClosePopover}
              sx={{ borderRadius: 1, px: 1, py: 0.75 }}
            >
              <Iconify icon="solar:user-id-bold" width={18} sx={{ mr: 1.5 }} />
              Impersonate
            </MenuItem>
          )}

          {canImpersonate && (
            <MenuItem
              component={Link}
              href={'/account/feature-access'}
              onClick={handleClosePopover}
              sx={{ borderRadius: 1, px: 1, py: 0.75 }}
            >
              <Iconify icon="solar:shield-user-bold" width={18} sx={{ mr: 1.5 }} />
              Feature access
            </MenuItem>
          )}

          <MenuItem
            component={Link}
            href={'/changelog'}
            onClick={handleClosePopover}
            sx={{ borderRadius: 1, px: 1, py: 0.75 }}
          >
            <Iconify icon="solar:notes-bold" width={18} sx={{ mr: 1.5 }} />
            Release Notes
          </MenuItem>

          {canSeePlatformTour && (
            <MenuItem
              onClick={() => {
                handleClosePopover();
                openPlatformOnboarding();
              }}
              sx={{ borderRadius: 1, px: 1, py: 0.75 }}
            >
              <Iconify icon="solar:map-arrow-right-bold" width={18} sx={{ mr: 1.5 }} />
              Platform tour
            </MenuItem>
          )}

          {isAdmin && (
            <MenuItem
              component={Link}
              href={'/admin'}
              onClick={handleClosePopover}
              sx={{ borderRadius: 1, px: 1, py: 0.75 }}
            >
              <Iconify
                icon="solar:shield-user-bold"
                width={18}
                sx={{ mr: 1.5 }}
              />
              Admin
            </MenuItem>
          )}

          {canAccessMailhouse && (
            <MailhouseAxios>
              <MailhouseAdminMenuItem onNavigate={handleClosePopover} />
            </MailhouseAxios>
          )}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Box sx={{ p: 0.5 }}>
          <Tooltip
            title={
              isImpersonating ? 'Exit impersonation before signing out' : ''
            }
            placement="left"
          >
            <span>
              <MenuItem
                onClick={onLogout}
                disabled={isImpersonating}
                sx={{
                  borderRadius: 1,
                  px: 1,
                  py: 0.75,
                  color: isImpersonating ? 'text.disabled' : 'error.main',
                  fontWeight: 600,
                }}
              >
                <Iconify
                  icon="material-symbols:logout-rounded"
                  width={18}
                  sx={{ mr: 1.5 }}
                />
                Sign out
              </MenuItem>
            </span>
          </Tooltip>
        </Box>
      </Popover>
    </>
  );
}
